import { useEffect, useState } from "react";
import { WithClerkToken } from "@/components/Members";
import { PROP65, PROP65_IDS } from "@/data/singles";
import { Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStore } from "@/context/StoreContext";
import { track, gaItem } from "@/lib/analytics";
import { CURRENCY, api, money, errMsg, IMAGES, productImage, shipFeeLines, shipLabel, shipCountries, memberPrice, SHIP_ZONES } from "@/lib/api";
import { AudNote } from "@/components/Usd";
import { SHIP_OPTIONS, US_STATES, BLOCKED_STATES, BLOCKED_MSG } from "@/data/content";
import { LegalLinks } from "@/components/EmailPopup";
import { DiscountBox, useDiscount, discounted } from "@/components/DiscountBox";
import { MASCOT_ALT } from "@/data/seo";

export const QtyStepper = ({ value, onChange, max, prefix = "quantity" }) => (
  <div className="inline-flex items-center rounded-full border border-navy/15 bg-white">
    <button data-testid={`${prefix}-decrement-button`} onClick={() => onChange(value - 1)} className="h-11 w-11 grid place-items-center hover:bg-foam rounded-l-full transition-colors" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
    <span data-testid={`${prefix}-value`} className="w-8 text-center font-display">{value}</span>
    <button data-testid={`${prefix}-increment-button`} onClick={() => onChange(value + 1)} disabled={value >= max} className="h-11 w-11 grid place-items-center hover:bg-foam rounded-r-full transition-colors disabled:opacity-40" aria-label="Increase"><Plus className="h-4 w-4" /></button>
  </div>
);

const PICKER = [...SHIP_OPTIONS, ...[["GB", "United Kingdom"], ["DE", "Germany"], ["ES", "Spain"], ["IE", "Ireland"], ["JP", "Japan"], ["XX", "Other country"]].map(([code, name]) => ({ code, name }))];

const MemberProbe = ({ getToken, setMember }) => {
  useEffect(() => {
    getToken().then((t) => (t ? api.get("/account/membership", { headers: { Authorization: `Bearer ${t}` } }).then(({ data }) => setMember(!!data.member)) : setMember(false))).catch(() => setMember(false));
  }, [getToken, setMember]);
  return null;
};

export const unitPrice = (p, member) => (member && p.member_discount !== false ? memberPrice(p) : Number(p.price_usd));

export const CountryPicker = ({ country, setCountry, allowed, usOnlyName = "" }) => (
  <div>
    <label className="font-mono text-xs uppercase tracking-widest">Shipping to</label>
    <Select value={country} onValueChange={setCountry}>
      <SelectTrigger data-testid="country-select" className="mt-2 h-12 rounded-xl border border-navy/15 bg-white font-semibold"><SelectValue placeholder="Pick a country" /></SelectTrigger>
      <SelectContent>
        {PICKER.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
    {country && !allowed.includes(country) && (
      <p data-testid="unsupported-country-warning" role="alert" className="mt-3 rounded-xl border border-navy/15 bg-coral text-white p-3 text-sm font-medium">{usOnlyName && SHIP_ZONES[country] ? `${usOnlyName} is US only.` : BLOCKED_MSG}</p>
    )}
  </div>
);

const CheckoutBtn = ({ onClick, disabled, loading }) => (
  <button data-testid="checkout-button" onClick={onClick} disabled={disabled} className="btn-pop bg-sunny w-full">
    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Checkout"}
  </button>
);

export const CartDrawer = () => {
  const { settings, items, count, updateQty, maxFor, cartOpen, setCartOpen, cartCountry: country, setCartCountry: setCountry, cartState: state, setCartState: setState, member, setMember } = useStore();
  const [loading, setLoading] = useState(false);
  const allowed = shipCountries(settings);
  const stateBlocked = BLOCKED_STATES.includes(state);
  const d = useDiscount(cartOpen);
  const pct = d.applied?.pct || 0;
  const welcome = d.applied?.code === "WELCOME10";
  const unit = (p) => (welcome ? unitPrice(p, member) : discounted(unitPrice(p, member), pct));
  const gross = items.reduce((s, i) => s + unit(i.product) * i.qty, 0);
  const subtotal = welcome ? (Math.round(gross * 100) - Math.round(Math.round(gross * 100) * pct / 100)) / 100 : gross;
  const welcomeBlock = welcome && (member ? "WELCOME10 can't be combined with Captain's Club member prices."
    : items.some((i) => i.product.kind === "stack") ? "WELCOME10 doesn't apply to bundles, stacks or VIP packs."
      : items.some((i) => i.qty > 1) ? "WELCOME10 applies to one of each item (no multi-buys)." : "");
  const fee = shipFeeLines(country, items.map((i) => [i.product, i.qty]));
  const usOnly = items.filter((i) => i.product.ship_countries && !i.product.ship_countries.includes(country)).map((i) => i.product.short_name);
  const canCheckout = count > 0 && allowed.includes(country) && !usOnly.length && (country !== "US" || (state && !stateBlocked));

  const checkout = async (getToken) => {
    setLoading(true);
    try {
      const token = getToken ? await getToken() : null;
      const disc = d.applied ? { discount_code: d.applied.code, email: d.email } : {};
      const { data } = await api.post("/checkout", { items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })), country, state: country === "US" ? state : "", origin_url: window.location.origin, ...disc }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      track("begin_checkout", { currency: CURRENCY, value: Number(subtotal.toFixed(2)), ...(d.applied ? { coupon: d.applied.code } : {}),
        items: items.map((i) => ({ ...gaItem(i.product, i.qty), price: Number(unit(i.product).toFixed(2)) })) });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Checkout wiped out. Try again."));
      setLoading(false);
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-cream border-l border-navy/10 w-full sm:max-w-md flex flex-col">
        <SheetTitle className="font-display text-3xl uppercase">Your stash</SheetTitle>
        {count === 0 ? (
          <div data-testid="cart-empty-state" className="flex-1 grid place-items-center text-center">
            <div>
              <img src={IMAGES.mascotSmall} alt={MASCOT_ALT} loading="lazy" decoding="async" width="128" height="128" className="w-32 mx-auto rounded-full border border-navy/15" />
              <p className="mt-4 font-display text-xl uppercase">Cart's emptier than the beach at 6am.</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 mt-4">
            {items.map(({ product: p, qty }) => (
              <div key={p.id} data-testid={`cart-line-${p.id}`} className="sticker-sm bg-white p-3 flex gap-3">
                <img src={productImage(p.id, 400)} alt={p.name} loading="lazy" decoding="async" width="80" height="80" className="h-20 w-20 rounded-lg object-cover border border-navy/15" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold leading-tight">{p.name}</p>
                  <p data-testid={`cart-line-price-${p.id}`} className="text-sm text-slate-600">{unit(p) !== Number(p.price_usd) ? <><s>{money(p.price_usd)}</s> {money(unit(p))}</> : money(p.price_usd)} · {p.count} {p.unit}</p>
                  {p.member_discount === false && <p data-testid={`cart-member-na-${p.id}`} className="text-xs text-slate-600">Member discount n/a</p>}
                  <div className="mt-2 flex items-center justify-between">
                    <QtyStepper value={qty} onChange={(n) => updateQty(p.id, n)} max={maxFor(p.id)} prefix={`cart-quantity-${p.id}`} />
                    <button data-testid={`cart-remove-${p.id}`} onClick={() => updateQty(p.id, 0)} className="p-2 hover:text-coral" aria-label="Remove"><Trash2 className="h-5 w-5" /></button>
                  </div>
                </div>
              </div>
            ))}
            <CountryPicker country={country} setCountry={setCountry} allowed={allowed} />
            {usOnly.length > 0 && <p data-testid="cart-us-only-warning" className="rounded-xl border border-navy/15 bg-sand p-3 text-sm font-medium">{usOnly.join(", ")} {usOnly.length > 1 ? "are" : "is"} US only. Remove {usOnly.length > 1 ? "them" : "it"} or switch to the US to check out.</p>}
            {country === "US" && <StatePicker state={state} setState={setState} />}
            {country === "US" && state === "CA" && items.some((i) => PROP65_IDS.includes(i.product.id)) && (
              <p data-testid="cart-prop65-warning" role="alert" className="rounded-xl border-2 border-navy bg-sunny/30 p-3 text-xs font-medium">California (Prop 65), Moringa Leaf Capsules: {PROP65}</p>
            )}
            <DiscountBox d={d} />
            {welcomeBlock && <p data-testid="cart-welcome-blocked" role="alert" className="rounded-xl border border-navy/15 bg-sand p-3 text-sm font-medium">{welcomeBlock} Remove the code to check out.</p>}
          </div>
        )}
        {count > 0 && (
          <div className="border-t border-navy/10 pt-4 space-y-3">
            <div className="flex justify-between font-display text-xl uppercase"><span>Subtotal{pct ? ` (${pct}% off)` : ""}</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
            {member && <p data-testid="cart-member-note" className="text-xs font-semibold text-navy">Captain's Club member prices applied.</p>}
            <p data-testid="cart-shipping-line" className="text-xs text-slate-600">{allowed.includes(country) && !stateBlocked ? <>+ {shipLabel(country)} {money(fee)}. Total {money(subtotal + fee)}.</> : BLOCKED_MSG} Secure checkout by Stripe. Please use a shipping address in the country picked above.</p>
            <AudNote className="text-slate-600" testId="cart-currency-note" />
            <LegalLinks text="By checking out you agree to our" className="text-slate-600" refunds />
            <WithClerkToken render={(getToken) => <><MemberProbe getToken={getToken} setMember={setMember} /><CheckoutBtn onClick={() => checkout(getToken)} disabled={!canCheckout || loading || !!welcomeBlock} loading={loading} /></>} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const StatePicker = ({ state, setState }) => (
  <div>
    <label className="font-mono text-xs uppercase tracking-widest">State</label>
    <Select value={state} onValueChange={setState}>
      <SelectTrigger data-testid="state-select" className="mt-2 h-12 rounded-xl border border-navy/15 bg-white font-semibold"><SelectValue placeholder="Pick your state" /></SelectTrigger>
      <SelectContent>{US_STATES.map((s) => <SelectItem key={s} value={s} data-testid={`state-option-${s}`}>{s}</SelectItem>)}</SelectContent>
    </Select>
    {BLOCKED_STATES.includes(state) && (
      <p data-testid="state-blocked-warning" className="mt-3 rounded-xl border border-navy/15 bg-coral text-white p-3 text-sm">{BLOCKED_MSG}</p>
    )}
  </div>
);
